import type { LivePerformance, MediaKey } from "./types";

/**
 * Derive a display title from a filename slug — e.g. "cant-sleep-at-night"
 * becomes "CANT SLEEP AT NIGHT". Used so the live archive entries below
 * stay in sync with the R2 object keys (which match the source MP4
 * filenames). Edit individual entries' `title` field below to override.
 */
function titleFromSlug(slug: string): string {
  return slug.replace(/-/g, " ").toUpperCase();
}

function videoKey(slug: string): MediaKey {
  return `public/video/live/${slug}.mp4`;
}

function posterKey(slug: string): MediaKey {
  return `public/video/live/${slug}-poster.jpg`;
}

function previewKey(slug: string): MediaKey {
  return `public/video/live/${slug}-preview.mp4`;
}

function clip(slug: string, opts: Partial<LivePerformance> = {}): LivePerformance {
  return {
    slug,
    title: opts.title ?? titleFromSlug(slug),
    date: opts.date ?? "2026-05-26",
    venue: opts.venue ?? "VRChat",
    format: opts.format ?? "vr",
    description: opts.description,
    video: opts.video ?? {
      kind: "file",
      ref: { kind: "public", key: videoKey(slug) },
      posterKey: posterKey(slug),
      mime: "video/mp4",
    },
    cover: opts.cover ?? {
      ref: { kind: "public", key: posterKey(slug) },
      alt: `${opts.title ?? titleFromSlug(slug)} — poster`,
      width: 1280,
      height: 720,
    },
    previewKey: opts.previewKey ?? previewKey(slug),
    durationSec: opts.durationSec,
    tracklist: opts.tracklist,
  };
}

// VR DJ-set clips. Each filename maps to a slug; the title is derived but
// can be overridden per-entry. Order = strongest first.
export const liveArchive: readonly LivePerformance[] = [
  clip("its-a-beat-like-soliloquy-live"),
  clip("while-you-were-sleeping-in-the-city-mashup"),
  clip("become-zeet-noise"),
  clip("cant-sleep-at-night"),
  clip("mau5-about-you-all-the-time"),
  clip("pwah-live-left-to-right"),
  clip("watch-me-work-the-vibe"),
  clip("stimulate-rapture-mash-up"),
  clip("if-you-love-me-live"),
  clip("pull-up-live"),
];

export function getLive(slug: string): LivePerformance | undefined {
  return liveArchive.find((l) => l.slug === slug);
}

export function getFeaturedLive(): LivePerformance | undefined {
  const vr = liveArchive.find((l) => l.format === "vr");
  return vr ?? liveArchive[0];
}
